import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    /** Delay in milliseconds, for staggering siblings. */
    delay?: number
    /** Distance the content rises from, in pixels. */
    distance?: number
}

/** Fades and lifts its content into place the first time it scrolls into view. */
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0, distance = 28 }) => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (!('IntersectionObserver' in window)) {
            setVisible(true)

            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return

            setVisible(true)
            observer.disconnect()
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            data-reveal=''
            className={cn(
                'transition-[opacity,transform] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity motion-reduce:duration-300',
                visible ? 'opacity-100' : 'opacity-0',
                className
            )}
            style={{
                transitionDelay: `${delay}ms`,
                transform: visible ? 'none' : `translate3d(0, ${distance}px, 0)`
            }}
        >
            {children}
        </div>
    )
}
